import React, { useEffect, useState } from "react";
import { fetchPricing } from "../lib/api";
import { Check, Sparkles, ArrowUpRight, X } from "lucide-react";
import { useScrollReveal } from "../hooks/useScrollReveal";

const FALLBACK_PLANS = [
  {
    id: "fallback-price-1",
    name: "Spark",
    price: "$1,499",
    cadence: "/month",
    blurb: "Single workflow, fully managed",
    features: ["1 automation", "Slack support", "Monthly review"],
    highlight: false,
    order: 1,
  },
  {
    id: "fallback-price-2",
    name: "Scale",
    price: "$4,999",
    cadence: "/month",
    blurb: "Multi-workflow growth stack",
    features: ["Up to 6 automations", "Priority engineer", "Bi-weekly strategy"],
    highlight: true,
    order: 2,
  },
  {
    id: "fallback-price-3",
    name: "Enterprise",
    price: "Let's talk",
    cadence: "",
    blurb: "Custom AI infra at scale",
    features: ["Unlimited workflows", "Dedicated pod", "SLA + SOC2"],
    highlight: false,
    order: 3,
  },
];

export const Pricing = ({ onOpenLead }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const revealRef = useScrollReveal(".in-view", [plans]);

  useEffect(() => {
    let active = true;
    let timer;

    const load = () => {
      fetchPricing()
        .then((d) => {
          if (!active) return;
          const fetched = d && (Array.isArray(d.plans) ? d.plans : (Array.isArray(d) ? d : null));
          setPlans(fetched && fetched.length > 0 ? fetched : FALLBACK_PLANS);
          setLoading(false);
          timer = setTimeout(load, 30000); // Poll every 30s
        })
        .catch(() => {
          if (!active) return;
          setPlans((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : FALLBACK_PLANS));
          setLoading(false);
          timer = setTimeout(load, 10000); // Retry sleeping backend in 10s
        });
    };

    load();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, []);

  const planList = Array.isArray(plans) ? plans : FALLBACK_PLANS;

  return (
    <div ref={revealRef} className="pt-28 pb-10" data-testid="pricing-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 text-center">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00F0FF] font-bold">
          Pricing
        </p>
        <h1 className="font-display text-5xl md:text-7xl font-black text-white mt-4 leading-[0.9] tracking-tighter">
          Pay for <span className="text-gradient-neon">leverage</span>.<br />
          Not seats.
        </h1>
        <p className="max-w-xl mx-auto text-zinc-400 mt-6 text-base">
          Flat-fee, fully managed automation. Cancel anytime. Yes, really.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-96 rounded-3xl glass animate-pulse"
              />
            ))
          : planList.map((p) => (
              <div
                key={p.id}
                data-testid={`pricing-plan-${p.id}`}
                onClick={() => setSelectedPlan(p)}
                className={`in-view relative rounded-3xl p-8 border transition-colors cursor-pointer ${
                  p.highlight
                    ? "bg-[#00F0FF]/[0.04] border-[#00F0FF]/40 hover:border-[#00F0FF]"
                    : "bg-white/[0.02] border-white/10 hover:border-white/30"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#00F0FF] text-black text-[10px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Most popular
                  </div>
                )}
                <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 font-bold">
                  {p.name}
                </p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-black text-white tracking-tight">
                    {p.price}
                  </span>
                  <span className="text-zinc-500 text-sm">{p.cadence}</span>
                </div>
                <p className="text-zinc-400 text-sm mt-2">{p.blurb}</p>

                <ul className="mt-6 space-y-2">
                  {p.features && Array.isArray(p.features) && p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-zinc-200 text-sm"
                    >
                      <Check
                        className={`w-4 h-4 min-w-4 shrink-0 ${
                          p.highlight ? "text-[#00F0FF]" : "text-zinc-300"
                        }`}
                      />
                      <span className="truncate">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenLead?.();
                  }}
                  data-testid={`pricing-cta-${p.id}`}
                  className={`mt-8 w-full py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors ${
                    p.highlight
                      ? "bg-[#00F0FF] text-black hover:bg-[#00C8E0]"
                      : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                  }`}
                >
                  Get started <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ))}
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 mt-24">
        <h3 className="font-display text-3xl font-black text-white tracking-tight">
          Frequently asked questions
        </h3>
        <div className="mt-8 space-y-4">
          {[
            {
              q: "What if I don't know what to automate?",
              a: "Book the free audit. We map your workflows and tell you exactly where the leverage is hiding.",
            },
            {
              q: "Is there a setup fee?",
              a: "No. Onboarding, scoping, and your first sprint are bundled into month one.",
            },
            {
              q: "Who owns the code?",
              a: "You do. Everything we build is delivered into your repos and infra.",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="in-view glass rounded-2xl p-6"
              data-testid={`pricing-faq-${i}`}
            >
              <p className="text-white font-semibold">{f.q}</p>
              <p className="text-zinc-400 mt-2 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Plan Details Modal */}
      {selectedPlan && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={() => setSelectedPlan(null)}
          />
          <div className="relative w-full max-w-lg overflow-y-auto glass-strong rounded-3xl p-8 md:p-10 shadow-[0_0_80px_rgba(0,240,255,0.18)]">
            {/* Close Button */}
            <button
              onClick={() => setSelectedPlan(null)}
              className="absolute top-6 right-6 text-zinc-400 hover:text-white"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Label */}
            <p className="text-xs tracking-[0.2em] uppercase text-[#00F0FF] font-bold mb-4">
              Plan Details
            </p>

            {/* Plan Title */}
            <h3 className="font-display text-4xl font-black text-white leading-tight">
              {selectedPlan.name}
            </h3>

            {/* Price badge */}
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-5xl font-black text-[#00F0FF] tracking-tight">
                {selectedPlan.price}
              </span>
              <span className="text-zinc-400 text-sm">{selectedPlan.cadence}</span>
            </div>
            
            <p className="text-zinc-300 text-sm mt-3 leading-relaxed">
              {selectedPlan.blurb}
            </p>

            {/* Features section */}
            <div className="mt-8 border-t border-white/10 pt-6">
              <h4 className="text-xs tracking-wider uppercase text-zinc-400 font-bold mb-4">
                What's Included
              </h4>
              <ul className="space-y-3">
                {selectedPlan.features && Array.isArray(selectedPlan.features) && selectedPlan.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 text-zinc-200 text-sm leading-relaxed"
                  >
                    <Check className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Action button */}
            <button
              onClick={() => {
                setSelectedPlan(null);
                onOpenLead?.();
              }}
              className="w-full mt-8 bg-[#00F0FF] text-black font-bold py-4 rounded-xl hover:bg-[#00C8E0] transition-colors flex items-center justify-center gap-2"
            >
              Get started <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
